import React, { useContext, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

import { StyledButton } from "../components";
import { LangContext } from "../contexts/langContext";
import { SelectAnswerContext } from "../contexts/selectAnswerContext";
import { MatchStatementsQuestion, TranslationData } from "@common";

interface RenderProps extends MatchStatementsQuestion {
  readOnly?: boolean;
  showCorrect?: boolean;
}

const SeveralOption: React.FC<{
  [key in keyof RenderProps]: RenderProps[key];
}> = ({
  pair1TextLeft,
  pair1TextRight,
  pair2TextLeft,
  pair2TextRight,
  pair3TextLeft,
  pair3TextRight,
  pair4TextLeft,
  pair4TextRight,
  readOnly = false,
  showCorrect = false,
}) => {
  const { translateTranslationData, translatePageText } = useContext(LangContext);
  const { onSelectAnswer } = useContext(SelectAnswerContext);

  const [leftSelection, setLeftSelection] = useState("");
  const [rightSelection, setRightSelection] = useState("");

  const [matchStatmentPairs, setMatchStatmentPairs] = useState([
    { left: pair1TextLeft, right: pair1TextRight, correct: false },
    { left: pair2TextLeft, right: pair2TextRight, correct: false },
    { left: pair3TextLeft, right: pair3TextRight, correct: false },
    { left: pair4TextLeft, right: pair4TextRight, correct: false },
  ]);

  const [incorrectStatements, setIncorrectStatements] = useState<Array<string>>([]);

  const handleClick = (isRight: boolean, text: string) => {
    let newLeftSelection = leftSelection;
    let newRightSelection = rightSelection;
    if (isRight === true) {
      newRightSelection = text;
    } else {
      newLeftSelection = text;
    }

    if (newLeftSelection !== "" && newRightSelection !== "") {
      let correctAt = -1;
      matchStatmentPairs.forEach((pairData: any, index: number) => {
        if (
          translateTranslationData(pairData.left) === newLeftSelection &&
          translateTranslationData(pairData.right) === newRightSelection
        ) {
          correctAt = index;
        }
      });

      const prevMatchStatmentPairs = [...matchStatmentPairs];

      if (correctAt !== -1) {
        matchStatmentPairs[correctAt].correct = true;
        setMatchStatmentPairs(prevMatchStatmentPairs);
      } else {
        setIncorrectStatements([...incorrectStatements, newRightSelection, newLeftSelection]);
        setTimeout(() => {
          const newIncorrectStatements = [...incorrectStatements];
          const removeLeftIndex = incorrectStatements.indexOf(newLeftSelection);
          if (removeLeftIndex !== -1) {
            newIncorrectStatements.splice(removeLeftIndex, 1);
          }
          const removeRightIndex = incorrectStatements.indexOf(newRightSelection);
          if (removeRightIndex !== -1) {
            newIncorrectStatements.splice(removeRightIndex, 1);
          }
          setIncorrectStatements(newIncorrectStatements);
        }, 500);
      }

      let allDone = true;
      prevMatchStatmentPairs.forEach(
        (pairData: { left: TranslationData; right: TranslationData; correct: boolean }) => {
          if (pairData.left.sv !== "" && pairData.right.sv !== "" && pairData.correct === false) {
            allDone = false;
          }
        }
      );
      if (allDone === true) {
        const answerStatsValues = { val1: 0, val2: 1, val3: 0 };
        onSelectAnswer("correct", answerStatsValues);
      }

      newLeftSelection = "";
      newRightSelection = "";
    }

    setLeftSelection(newLeftSelection);
    setRightSelection(newRightSelection);
  };

  return (
    <>
      <div style={{ display: "flex" }}>
        <StatementColumn
          matchStatmentPairs={matchStatmentPairs}
          incorrectStatements={incorrectStatements}
          handleClick={handleClick}
          right={false}
          leftSelection={leftSelection}
          rightSelection={rightSelection}
          disabled={readOnly}
        />
        <StatementColumn
          matchStatmentPairs={matchStatmentPairs}
          incorrectStatements={incorrectStatements}
          handleClick={handleClick}
          right={true}
          leftSelection={leftSelection}
          rightSelection={rightSelection}
          disabled={readOnly}
        />
      </div>
      <div>
        <StyledButton
          hasCooldown
          variant="secondary"
          onClick={() => {
            const answerStatsValues = { val1: 0, val2: 0, val3: 1 };
            onSelectAnswer("incorrect", answerStatsValues);
          }}
          style={{ margin: "15px auto", display: "block" }}
          label={translatePageText("general.dontKnowContinue")}
          disabled={readOnly}
        />
      </div>
    </>
  );
};

const StatementColumn: React.FC<{
  matchStatmentPairs: any;
  incorrectStatements: any;
  handleClick: (isRight: boolean, text: any) => void;
  leftSelection: any;
  rightSelection: any;
  right: boolean;
  disabled?: boolean;
}> = ({
  matchStatmentPairs,
  incorrectStatements,
  handleClick,
  leftSelection,
  rightSelection,
  right,
  disabled,
}) => {
  const { translateTranslationData } = useContext(LangContext);
  const pairs = right ? [...matchStatmentPairs].reverse() : [...matchStatmentPairs];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <div style={{ margin: "0 6px" }}>
      {pairs.map((pair: any, index: number) => {
        const translationData = right ? pair.right : pair.left;
        const text = translateTranslationData(translationData);
        const isIncorrect = incorrectStatements.includes(text);
        const selected = right ? rightSelection === text : leftSelection === text;
        const isCorrect: boolean = pair.correct;

        const background: any = isIncorrect
          ? "red"
          : selected || isCorrect
          ? "lightgreen"
          : "rgba(255,255,255,0.8)";
        const border: any = isIncorrect
          ? "solid 3px red"
          : selected || isCorrect
          ? "solid 3px lightgreen"
          : "solid 3px rgba(0,0,0,0)";
        const color: any = isIncorrect
          ? "rgba(0,0,0,0.9)"
          : selected || isCorrect
          ? "green"
          : "rgba(0,0,0,0.9)";

        if (text === "") return <React.Fragment key={Math.random() * index}></React.Fragment>;

        return (
          <button
            key={text}
            onClick={() => (disabled ? undefined : handleClick(right, text))}
            style={{
              margin: "4px 0",
              padding: "0 14px",
              width: "100%",
              textAlign: "center",
              height: isMobile ? 128 : 150,
              borderRadius: 8,
              fontSize: isMobile ? 14 : 16,
              background,
              border,
              color,
              filter: "none",
            }}
          >
            {text}
          </button>
        );
      })}
    </div>
  );
};

export default SeveralOption;
